import React, { useContext } from 'react'
import { Link } from 'react-router-dom'
import { CodeBracketIcon, ChatBubbleLeftIcon, ChatBubbleLeftEllipsisIcon, ScissorsIcon, PhotoIcon, SparklesIcon, TrashIcon, FlagIcon } from '@heroicons/react/24/outline'
import { safeImageSrc, safeLinkHref } from '../../lib/safeUrl'

// ---------------------------------------------------------------------------
// Branch marker type (passed in from parent)
// ---------------------------------------------------------------------------
export interface BranchMarker {
  id: string
  title: string
  category: string
  wordCount: number
  rating: number
}

// ---------------------------------------------------------------------------
// Origin story info type (for character highlighting)
// ---------------------------------------------------------------------------
export interface OriginStoryInfo {
  _id: string
  title: string
  wordCount: number
  rating: number
}

// ---------------------------------------------------------------------------
// Character highlight context
// ---------------------------------------------------------------------------
interface CharacterHighlightCtx {
  characterNames: string[]
  onCharacterClick: (name: string, e: React.MouseEvent) => void
}
const CharHighlightCtx = React.createContext<CharacterHighlightCtx | null>(null)

function renderTextWithHighlights(
  text: string,
  names: string[],
  onClick: (name: string, e: React.MouseEvent) => void,
): React.ReactNode {
  const escaped = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  const re = new RegExp(`\\b(${escaped.join('|')})\\b`, 'gi')
  const parts: React.ReactNode[] = []
  let last = 0
  let match: RegExpExecArray | null
  let key = 0
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index))
    const name = match[0]
    parts.push(
      <button
        key={key++}
        type="button"
        onClick={(e) => onClick(name, e)}
        className="inline border-b-2 border-dashed border-amber-400 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-sm cursor-pointer transition-colors leading-none"
      >
        {name}
      </button>,
    )
    last = match.index + name.length
  }
  if (last < text.length) parts.push(text.slice(last))
  return parts.length === 0 ? text : <>{parts}</>
}

// ---------------------------------------------------------------------------
// Minimal Slate content types (mirrors editor output, no Slate dependency)
// ---------------------------------------------------------------------------
interface SlateText {
  text: string
  bold?: boolean
  italic?: boolean
  underline?: boolean
  code?: boolean
  strikethrough?: boolean
}

interface SlateElement {
  type: string
  url?: string
  alt?: string
  children: (SlateText | SlateElement)[]
}

type SlateNode = SlateText | SlateElement

function isText(node: SlateNode): node is SlateText {
  return typeof (node as SlateText).text === 'string'
}

// ---------------------------------------------------------------------------
// Leaf renderer
// ---------------------------------------------------------------------------
const Leaf: React.FC<{ leaf: SlateText; index: number }> = ({ leaf, index }) => {
  const charCtx = useContext(CharHighlightCtx)

  // Build base content — apply character highlights if any names are present
  let children: React.ReactNode
  if (charCtx && charCtx.characterNames.length > 0 && leaf.text) {
    children = renderTextWithHighlights(leaf.text, charCtx.characterNames, charCtx.onCharacterClick)
  } else {
    children = leaf.text || '\u00A0'
  }

  if (leaf.bold) children = <strong className="font-bold">{children}</strong>
  if (leaf.italic) children = <em className="italic">{children}</em>
  if (leaf.underline) children = <u className="underline decoration-current">{children}</u>
  if (leaf.strikethrough) children = <s className="line-through">{children}</s>
  if (leaf.code)
    children = (
      <code className="font-mono text-[0.875em] bg-gray-100 dark:bg-gray-800 text-rose-600 dark:text-rose-400 rounded px-1 py-0.5">
        {children}
      </code>
    )

  return <span key={index}>{children}</span>
}

// ---------------------------------------------------------------------------
// Recursive node renderer
// ---------------------------------------------------------------------------
const RenderNode: React.FC<{ node: SlateNode; index: number }> = ({ node, index }) => {
  if (isText(node)) return <Leaf leaf={node} index={index} />

  const children = node.children.map((child, i) => (
    <RenderNode key={i} node={child as SlateNode} index={i} />
  ))

  switch (node.type) {
    case 'heading-one':
      return (
        <h1 key={index} className="text-3xl font-bold text-gray-900 dark:text-gray-50 mt-10 mb-4 leading-tight">
          {children}
        </h1>
      )
    case 'heading-two':
      return (
        <h2 key={index} className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mt-8 mb-3 leading-snug">
          {children}
        </h2>
      )
    case 'heading-three':
      return (
        <h3 key={index} className="text-xl font-semibold text-gray-800 dark:text-gray-100 mt-6 mb-2">
          {children}
        </h3>
      )
    case 'block-quote':
      return (
        <blockquote
          key={index}
          className="border-l-4 border-primary-400 dark:border-primary-500 pl-5 my-6 italic text-gray-600 dark:text-gray-400"
        >
          {children}
        </blockquote>
      )
    case 'bulleted-list':
      return (
        <ul key={index} className="list-disc list-outside pl-6 my-4 space-y-1">
          {children}
        </ul>
      )
    case 'numbered-list':
      return (
        <ol key={index} className="list-decimal list-outside pl-6 my-4 space-y-1">
          {children}
        </ol>
      )
    case 'list-item':
      return <li key={index}>{children}</li>
    case 'image':
      return (
        <figure key={index} className="my-6 text-center">
          <img
            src={safeImageSrc(node.url) ?? ''}
            alt={node.alt ?? ''}
            className="max-w-full mx-auto rounded-lg shadow-md"
            loading="lazy"
          />
          {node.alt && (
            <figcaption className="mt-2 text-sm text-gray-500 dark:text-gray-400 italic">
              {node.alt}
            </figcaption>
          )}
        </figure>
      )
    case 'link':
      return (
        <a
          key={index}
          href={safeLinkHref(node.url)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 dark:text-primary-400 underline hover:opacity-75"
        >
          {children}
        </a>
      )
    default:
      // paragraph + any unknown type
      return (
        <p key={index} className="my-0 leading-[inherit]">
          {children}
        </p>
      )
  }
}

// ---------------------------------------------------------------------------
// Public SlateRenderer component
// ---------------------------------------------------------------------------
export interface SlateRendererProps {
  content: unknown[]
  fontSize?: 'sm' | 'base' | 'lg' | 'xl'
  fontFamily?: 'serif' | 'sans'
  className?: string
  /** Map of node-index → branches that diverge after that node */
  branchMarkers?: Map<number, BranchMarker[]>
  /** Character names to highlight in text (from origin-story branches) */
  characterNames?: string[]
  /** Called when user clicks a highlighted character name */
  onCharacterClick?: (name: string, e: React.MouseEvent) => void
  /** When true, show per-paragraph comment icons */
  inProgress?: boolean
  /** Map of node-index → comment count */
  commentCounts?: Map<number, number>
  /** Called when user clicks the "add comment" icon */
  onAddComment?: (nodeIndex: number) => void
  /** Called when user clicks the "view comments" bubble */
  onViewComments?: (nodeIndex: number) => void
  /** Called when user clicks the per-paragraph "branch here" icon */
  onBranchFromParagraph?: (nodeIndex: number) => void
  /** Called when user clicks the per-paragraph "illustrate here" icon */
  onIllustrateFromParagraph?: (nodeIndex: number) => void
  /** Illustrations to render inline, after the node at each one's `position` */
  illustrations?: ReaderIllustration[]
  /** Called to remove an illustration (shown to author/collaborator/admin) */
  onRemoveIllustration?: (illustrationId: string) => void
  /** Called to report an illustration (shown to other signed-in readers) */
  onReportIllustration?: (illustrationId: string) => void
  /** Called when user clicks a branch marker (overrides default Link navigation) */
  onBranchClick?: (branchId: string) => void
}

export interface ReaderIllustration {
  _id: string
  url: string
  alt: string
  caption?: string | null
  position: number
  width?: number
  height?: number
  aiGenerated?: boolean
}

const InlineIllustrations: React.FC<{
  items: ReaderIllustration[]
  onRemove?: (id: string) => void
  onReport?: (id: string) => void
}> = ({ items, onRemove, onReport }) => (
  <div className="my-5 space-y-4">
    {items.map((im) => (
      <figure key={im._id} className="group/illus relative">
        <img
          src={im.url}
          alt={im.alt}
          loading="lazy"
          decoding="async"
          width={im.width}
          height={im.height}
          className="w-full h-auto rounded-xl shadow-md"
        />
        {im.aiGenerated && (
          <span
            className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-600/90 text-white text-[10px] font-semibold backdrop-blur-sm"
            title="This illustration was generated by an AI model."
          >
            <SparklesIcon className="w-3 h-3" /> AI
          </span>
        )}
        {(onRemove || onReport) && (
          <div className="absolute top-2 left-2 flex items-center gap-1 transition-opacity opacity-70 sm:opacity-0 sm:group-hover/illus:opacity-100 focus-within:opacity-100">
            {onReport && (
              <button
                onClick={() => onReport(im._id)}
                title="Report this illustration"
                className="p-1.5 rounded-full bg-black/55 text-white hover:bg-black/75 backdrop-blur-sm"
              >
                <FlagIcon className="w-4 h-4" />
              </button>
            )}
            {onRemove && (
              <button
                onClick={() => onRemove(im._id)}
                title="Remove this illustration"
                className="p-1.5 rounded-full bg-black/55 text-white hover:bg-red-600 backdrop-blur-sm"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
        {im.caption && (
          <figcaption className="mt-1.5 text-center text-sm text-gray-500 dark:text-gray-400 italic">
            {im.caption}
          </figcaption>
        )}
      </figure>
    ))}
  </div>
)

const CATEGORY_COLORS: Record<string, string> = {
  general: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  backstory: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 border-purple-200 dark:border-purple-800',
  'parallel-worlds': 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 border-green-200 dark:border-green-800',
  'expanded-reality': 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 border-orange-200 dark:border-orange-800',
}

const InlineBranchMarkers: React.FC<{ branches: BranchMarker[]; onBranchClick?: (branchId: string) => void }> = ({ branches, onBranchClick }) => (
  <div className="flex flex-wrap items-center gap-2 my-3 pl-0">
    <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1 shrink-0">
      <CodeBracketIcon className="w-3.5 h-3.5" />
      {branches.length === 1 ? '1 branch' : `${branches.length} branches`}
    </span>
    {branches.map((b) =>
      onBranchClick ? (
        <button
          key={b.id}
          onClick={() => onBranchClick(b.id)}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-opacity hover:opacity-80 cursor-pointer ${
            CATEGORY_COLORS[b.category] ?? CATEGORY_COLORS.general
          }`}
        >
          <CodeBracketIcon className="w-3 h-3" />
          {b.title.length > 32 ? b.title.slice(0, 32) + '…' : b.title}
        </button>
      ) : (
        <Link
          key={b.id}
          to={`/reading/${b.id}`}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-opacity hover:opacity-80 ${
            CATEGORY_COLORS[b.category] ?? CATEGORY_COLORS.general
          }`}
        >
          <CodeBracketIcon className="w-3 h-3" />
          {b.title.length > 32 ? b.title.slice(0, 32) + '…' : b.title}
        </Link>
      )
    )}
  </div>
)

const FONT_SIZE_CLASS = {
  sm: 'text-base leading-8',
  base: 'text-lg leading-9',
  lg: 'text-xl leading-10',
  xl: 'text-2xl leading-10',
}

const SlateRenderer: React.FC<SlateRendererProps> = ({
  content,
  fontSize = 'base',
  fontFamily = 'serif',
  className = '',
  branchMarkers,
  characterNames,
  onCharacterClick,
  inProgress,
  commentCounts,
  onAddComment,
  onViewComments,
  onBranchFromParagraph,
  onIllustrateFromParagraph,
  illustrations,
  onRemoveIllustration,
  onReportIllustration,
  onBranchClick,
}) => {
  const illusByPos = new Map<number, ReaderIllustration[]>()
  for (const im of illustrations ?? []) {
    const arr = illusByPos.get(im.position) ?? []
    arr.push(im)
    illusByPos.set(im.position, arr)
  }

  if (!Array.isArray(content) || content.length === 0) {
    return (
      <p className="text-gray-400 dark:text-gray-500 italic">
        This branch has no content yet.
      </p>
    )
  }

  const charCtxValue =
    characterNames && characterNames.length > 0 && onCharacterClick
      ? { characterNames, onCharacterClick }
      : null

  const inner = (
    <div
      className={`
        ${FONT_SIZE_CLASS[fontSize]}
        ${fontFamily === 'serif' ? 'font-serif' : 'font-sans'}
        text-gray-800 dark:text-gray-200
        space-y-[0.85em]
        ${className}
      `}
    >
      {(content as SlateNode[]).map((node, i) => (
        <React.Fragment key={i}>
          {(inProgress || !!onBranchFromParagraph || !!onIllustrateFromParagraph) ? (
            <div className="group relative">
              <RenderNode node={node} index={i} />
              {/* Per-paragraph action icons — overlaid (absolute) so they never narrow the text.
                  Anchored to the last line (bottom-right); on lg+ shifted into the right margin so
                  it doesn't overlap words at all. Faint on touch, hover-revealed on desktop,
                  always shown when commented. */}
              <div className={`absolute bottom-0 right-0 z-10 flex items-center gap-1 rounded-full bg-white/85 dark:bg-gray-900/85 backdrop-blur px-1 py-0.5 shadow-sm ring-1 ring-black/5 transition-opacity focus-within:opacity-100 lg:translate-x-[calc(100%+0.5rem)] ${
                (commentCounts?.get(i) ?? 0) > 0 ? 'opacity-100' : 'opacity-70 sm:opacity-0 sm:group-hover:opacity-100'
              }`}>
                {inProgress && (commentCounts?.get(i) ?? 0) > 0 && (
                  <button
                    onClick={() => onViewComments?.(i)}
                    className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 text-[10px] font-semibold hover:bg-primary-200 dark:hover:bg-primary-800/60 transition-colors"
                    title={`View ${commentCounts!.get(i)} comment${commentCounts!.get(i)! > 1 ? 's' : ''}`}
                  >
                    <ChatBubbleLeftEllipsisIcon className="w-3.5 h-3.5" />
                    {commentCounts!.get(i)}
                  </button>
                )}
                {inProgress && (
                  <button
                    onClick={() => onAddComment?.(i)}
                    className="p-1 rounded-full text-gray-400 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors"
                    title="Add comment"
                  >
                    <ChatBubbleLeftIcon className="w-4 h-4" />
                  </button>
                )}
                {!!onBranchFromParagraph && (
                  <button
                    onClick={() => onBranchFromParagraph(i)}
                    className="p-1 rounded-full text-gray-400 dark:text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
                    title="Branch from this paragraph"
                  >
                    <ScissorsIcon className="w-4 h-4" />
                  </button>
                )}
                {!!onIllustrateFromParagraph && (
                  <button
                    onClick={() => onIllustrateFromParagraph(i)}
                    className="p-1 rounded-full text-gray-400 dark:text-gray-500 hover:text-violet-600 dark:hover:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/30 transition-colors"
                    title="Illustrate here"
                  >
                    <PhotoIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ) : (
            <RenderNode node={node} index={i} />
          )}
          {illusByPos.has(i) && (
            <InlineIllustrations
              items={illusByPos.get(i)!}
              onRemove={onRemoveIllustration}
              onReport={onReportIllustration}
            />
          )}
          {branchMarkers?.has(i) && (
            <InlineBranchMarkers branches={branchMarkers.get(i)!} onBranchClick={onBranchClick} />
          )}
        </React.Fragment>
      ))}
    </div>
  )

  return charCtxValue ? (
    <CharHighlightCtx.Provider value={charCtxValue}>{inner}</CharHighlightCtx.Provider>
  ) : inner
}

export default SlateRenderer
